import { isNil, isEmpty, noop } from 'lodash/fp'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Flex } from 'rebass'

import { getFileWithName, isPDFFileType, getErrorByTypeName } from 'e-submission/apps/selectors'
import {
  removeFile,
  deletePhoto,
  browsePhoto,
  capturePhoto,
  previewPhoto,
  removeError,
} from 'e-submission/apps/actions'

import styled from 'styled-components'
import { Button, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import Dialog from 'e-submission/components/Dialog'
import Icon from 'e-submission/components/Icon'
import FormEngine from 'e-submission/components/FormEngine'
import waterMaskUrl from 'assets/images/water-mask.png'
import viewerUrl from 'file-loader?name=pdf-viewer/web/viewer.html&emitFile=false!e-submission/pdf-viewer/web/viewer.html'
import { isBlob } from 'e-submission/utils/pattern'
import { resolveImageFile } from 'e-submission/utils/promise'
import { getToggles } from 'quick-quote/feature-toggles'

export const WatermarkModal = styled(Dialog)`
  .modal-content {
    height: 100vh;
  }
  .modal-title {
    display: flex;
    flex: 1 0 100%;
    justify-content: space-between;
    align-items: center;
    button {
      padding: 5px 10px;
    }
  }
  .modal-body {
    display: flex;
    flex-direction: column;
    padding: 0 !important;
    iframe {
      flex: 1;
    }
  }
  .modal-footer {
    justify-content: space-between;
  }
  .formEngine {
    min-height: 260px;
  }
`
const onLoadIframe = (evt) => {
  const iframe = evt.target
  const iframeDocument = iframe.contentWindow.document
  const toolbar = iframeDocument.querySelector('.toolbar')
  const main = iframeDocument.querySelector('#mainContainer')
  const content = iframeDocument.querySelector('#viewerContainer')
  const customStyle = document.createElement('style')
  customStyle.type = 'text/css'
  customStyle.textContent = `
    .canvasWrapper:before {
      content: ' ';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      background-image: url(${waterMaskUrl});
      background-size: 200px;
      z-index: 10;
    }
  `
  iframeDocument.head.appendChild(customStyle)
  toolbar.style.visibility = 'hidden'
  content.style.top = 0
}

export const WithWatermark = styled(({ image, ...props }) => <Flex {...props} />)`
  position: relative;
  overflow: hidden;
  background-color: black;
  background-image: url('${({ image }) => image}');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  &:before {
    content: ' ';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-image: url(${waterMaskUrl});
    background-size: 200px;
    background-attachment: fixed;
  }
`

export const PhotoCard = ({
  editable,
  disabled,
  isOk,
  sections,
  setRef,
  getRef,
  name,
  accept = 'image/x-png,image/jpeg,application/pdf',
  checked,
  readOnly,
  setFileProps,
  validateFile = noop,
  CheckedComponent,
  NotCheckedComponent,
  isBrowseFile = !(typeof window === 'object' && !isEmpty(window.cordova)),
  isDisabledDeleteBtn,
  ...props
}) => {
  const stateApp = useSelector((state) => state)
  const url = getFileWithName(stateApp, name)
  const isPDF = isPDFFileType(stateApp, name)
  const isUploadError = !isNil(getErrorByTypeName(stateApp, name))

  const dispatch = useDispatch()

  const [isOpen, setIsOpen] = useState(false)

  let ref = useRef(null)

  const clearFile = () => {
    if (ref && ref.current && ref.current.value !== '') {
      ref.current.value = ''
    }
  }

  const openModal = () => {
    setIsOpen(true)
  }

  const closeModal = () => {
    clearFile()
    setIsOpen(false)
  }

  useEffect(() => {
    if (isOpen && isUploadError) {
      onClose()
    }
  }, [isUploadError])

  const browseFile = () => {
    beforeBrowseFile(() => {
      ref.current.click()
    })
  }

  const onDelete = () => {
    closeModal()
    dispatch(deletePhoto(name))
    if (!editable) {
      dispatch(removeFile(name))
    }
  }
  const onPreview = () => {
    if (checked) {
      openModal()
      dispatch(previewPhoto(name))
    }
  }
  const onClose = () => {
    closeModal()
    if (!editable) {
      dispatch(removeFile(name))
    }
  }
  const onOk = () => {
    const isOk = props.onOk ? props.onOk() : undefined
    if (isOk !== false) {
      closeModal()
      if (!editable) {
        dispatch(removeFile(name))
      }
    }
  }
  const onOpen = async (event) => {
    dispatch(removeError(name))
    if (props.onOpen) props.onOpen()

    const isShowPreview = !isNil(sections)

    if (isShowPreview) {
      openModal()
    }

    if (isBrowseFile) {
      const files = Array.from(event.target.files) || []
      const file = event.target.files[0]

      if (isBlob(file) && files.length > 0) {
        const format = await resolveImageFile(file)

        validateFile()
        dispatch(browsePhoto(name, files[0], isShowPreview, undefined, format))
      } else {
        validateFile()
      }
    } else {
      validateFile()
      dispatch(capturePhoto(name, isShowPreview))
    }

    if (editable) {
      clearFile()
    }
  }
  const beforeBrowseFile = (callback) => {
    if (getToggles().ENABLE_QR_PAYMENT_STATUS && props.callServiceBeforeBrowse) {
      dispatch({
        type: props.callServiceBeforeBrowse,
        payload: {
          isOnlyCheckQr: true,
          callbackCheckingQr: callback,
        },
      })
      return
    }
    callback()
  }

  return (
    <div>
      <WatermarkModal isOpen={isOpen} toggle={onClose}>
        <ModalHeader tag="div">
          <div className="btn-left" onClick={onClose}>
            <Icon.arrowLeft className="icon" />
          </div>
          <div>{props.description}</div>
          <div />
        </ModalHeader>
        <ModalBody>
          {!isPDF ? (
            <WithWatermark flex="1 1 100%" align="center" image={url} />
          ) : (
            <iframe src={`${viewerUrl}?file=${url}`} onLoad={onLoadIframe} />
          )}
          {sections && (
            <div className="formEngine">
              <FormEngine sections={sections} />
            </div>
          )}
        </ModalBody>
        {readOnly ? null : (
          <ModalFooter>
            <Button
              color={isDisabledDeleteBtn ? 'disabled' : 'primary'}
              outline
              onClick={onDelete}
              disabled={isDisabledDeleteBtn}
            >
              ลบ
            </Button>
            <Button color={!checked ? 'disabled' : 'primary'} onClick={onOk}>
              ตกลง
            </Button>
          </ModalFooter>
        )}
      </WatermarkModal>
      {checked ? (
        <CheckedComponent {...props} onClick={onPreview} />
      ) : (
        <div>
          <NotCheckedComponent
            {...props}
            validateFile={validateFile}
            disabled={disabled}
            onClick={() => !disabled && (isBrowseFile ? browseFile() : onOpen())}
          />

          <input
            ref={ref}
            type="file"
            onChange={onOpen}
            onClick={(e) => {
              e.stopPropagation()
            }}
            accept={accept}
          />
        </div>
      )}
    </div>
  )
}

export default PhotoCard
